import React, { FC, useEffect, useMemo } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { BodyText, FlexCol, FlexDynamic, FlexRow, H2Text, PrimaryButton } from '@minecraft.atoms';
import { AddressValidationInput, AddressValidationResult } from '@minecraft.graphql-operations';
import { RadioCardGroup, SelectInput, TextInput } from '@minecraft.molecules';
import { useGlobalNotification } from '@minecraft.notification-provider';
import Modal from '@minecraft.modal';
import Icon from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';
import { SuggestedAddress } from '@blocs.address-suggestion';
import { useBillingPopup } from '../BillingPopupContext';
import { useBillingStates } from '../Checkout/PaymentForm/useBillingStates';
import { toSelectOptions } from '../Checkout/PaymentForm/BillingAddressFormLayout';
import { createAddressValidationModalSchema } from '../Checkout/PaymentForm/ValidationSchema';
import { BillingAddressFields } from './formatters';

type AddressValidationFormValues = {
  validationLine1: string;
  validationLine2: string;
  validationCity: string;
  validationState: string;
  validationPostalCode: string;
  validationCountry: string;
};

type AddressValidationFormProps = {
  result: AddressValidationResult;
  currentAddress: BillingAddressFields;
  lastCheckedInput: AddressValidationInput;
  onSubmit: (formattedAddress: SuggestedAddress) => void;
  onDismiss: () => void;
  onSuggestionSelected?: () => void;
};

const requiredLabel = (label: string) => `${label} *`;

const RECOMMENDED_OPTION_VALUE = 'recommended';

const isSuggestionApplied = (
  values: AddressValidationFormValues,
  correctedAddress: AddressValidationResult['correctedAddress']
) =>
  Boolean(correctedAddress) &&
  values.validationCity === correctedAddress.locality &&
  values.validationState === correctedAddress.administrativeArea &&
  values.validationPostalCode === correctedAddress.postalCode &&
  values.validationCountry === correctedAddress.regionCode;

type AddressValidationFieldsProps = {
  result: AddressValidationResult;
  onSuggestionSelected?: () => void;
};

const AddressValidationFields: FC<AddressValidationFieldsProps> = ({ result, onSuggestionSelected }) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const { values, setValues, isSubmitting, isValid } = useFormikContext<AddressValidationFormValues>();
  const {
    countryOptions,
    billingCountriesLoading: countriesLoading,
    billingCountriesError: countriesError,
  } = useBillingPopup();
  const selectedCountry = countryOptions.find((option) => option.id === values.validationCountry);
  const { stateOptions, statesLoading, statesError } = useBillingStates(selectedCountry?.countryId);

  useEffect(() => {
    if (countriesError) {
      showMessage({ color: 'danger', message: t('common:paywall.checkout.billingAddress.countriesLoadError') });
    }
  }, [countriesError, showMessage, t]);

  useEffect(() => {
    if (statesError) {
      showMessage({ color: 'danger', message: t('common:paywall.checkout.billingAddress.statesLoadError') });
    }
  }, [showMessage, statesError, t]);

  const countrySelectOptions = useMemo(() => toSelectOptions(countryOptions), [countryOptions]);
  const stateSelectOptions = useMemo(() => toSelectOptions(stateOptions), [stateOptions]);

  const { correctedAddress } = result;
  const suggestionApplied = correctedAddress ? isSuggestionApplied(values, correctedAddress) : false;

  return (
    <Form>
      <FlexCol gap="5" mdProps={{ mt: '4' }}>
        <FlexRow
          alignItems="center"
          gap="2"
          borderPlacement="bottom"
          className="cn_atom_pb-4"
          w="full"
          mdProps={{ borderPlacement: 'none', className: '' }}
        >
          <Icon name="warning-banner" size="md" themeColor="danger" />
          <H2Text fontWeight="medium">{t('common:paywall.checkout.billingAddress.addressValidation.title')}</H2Text>
        </FlexRow>
        <BodyText>{t('common:paywall.checkout.billingAddress.addressValidation.description')}</BodyText>
        <SelectInput
          name="validationCountry"
          label={requiredLabel(t('common:paywall.checkout.billingAddress.countryOrRegion'))}
          options={countrySelectOptions}
          disabled={countriesLoading}
          onChange={() => setValues({ ...values, validationState: '' })}
        />
        <TextInput name="validationLine1" label={t('common:paywall.checkout.billingAddress.addressLine1')} required />
        <TextInput name="validationCity" label={t('common:form.label.city')} required />
        <FlexDynamic w="full" gap="3" mdProps={{ direction: 'row', wrap: 'nowrap' }}>
          <SelectInput
            name="validationState"
            label={requiredLabel(t('common:paywall.checkout.billingAddress.stateRegion'))}
            options={stateSelectOptions}
            disabled={statesLoading}
          />
          <TextInput name="validationPostalCode" label={t('common:paywall.checkout.billingAddress.zipCode')} required />
        </FlexDynamic>
        {correctedAddress && (
          <RadioCardGroup
            label={t('common:paywall.checkout.billingAddress.addressValidation.recommended')}
            flexDirection="column"
            value={suggestionApplied ? RECOMMENDED_OPTION_VALUE : ''}
            onChange={() => {
              setValues({
                ...values,
                validationLine1: correctedAddress.addressLines?.[0] || '',
                validationLine2: correctedAddress.addressLines?.[1] || '',
                validationCity: correctedAddress.locality || '',
                validationState: correctedAddress.administrativeArea || '',
                validationPostalCode: correctedAddress.postalCode || '',
                validationCountry: correctedAddress.regionCode || '',
              });
              onSuggestionSelected?.();
            }}
            options={[
              {
                value: RECOMMENDED_OPTION_VALUE,
                label: t('common:paywall.checkout.billingAddress.addressValidation.recommended'),
                icon: { name: 'spicerack-location', themeColor: 'primary', size: 'md' },
                bodyText: [
                  correctedAddress.addressLines?.join(' '),
                  correctedAddress.locality,
                  correctedAddress.administrativeArea,
                  correctedAddress.postalCode,
                ]
                  .filter(Boolean)
                  .join(', '),
              },
            ]}
          />
        )}
        <FlexCol w="full" mdProps={{ w: 'fit', alignSelf: 'end' }}>
          <PrimaryButton
            type="submit"
            label={t('common:button.continue')}
            disabled={!isValid || isSubmitting}
            isSmall
          />
        </FlexCol>
      </FlexCol>
    </Form>
  );
};

export const AddressValidationForm: FC<AddressValidationFormProps> = ({
  result,
  currentAddress,
  lastCheckedInput,
  onSubmit,
  onDismiss,
  onSuggestionSelected,
}) => {
  const { t } = useTranslation();

  const { initialValues, initialTouched } = useMemo(
    () => ({
      initialValues: {
        validationLine1: currentAddress?.address_line_1 || '',
        validationLine2: currentAddress?.address_line_2 || '',
        validationCity: currentAddress?.city || '',
        validationState: currentAddress?.state_region || '',
        validationPostalCode: currentAddress?.postal_code || '',
        validationCountry: currentAddress?.country || '',
      } as AddressValidationFormValues,
      // Every flagged field starts touched, so TextInput (which requires both `error` and `touched`
      // to render red for a `required` field) shows its error immediately on mount, not just once
      // blurred.
      initialTouched: {
        validationLine1: false,
        validationLine2: false,
        validationCity: true,
        validationState: true,
        validationPostalCode: true,
        validationCountry: true,
      } as Record<keyof AddressValidationFormValues, boolean>,
    }),
    [currentAddress]
  );

  if (currentAddress?.country !== 'US') return null;

  return (
    <Modal
      isOpen
      data-testid="address-validation-modal"
      handleToggleClick={onDismiss}
      modalSize="small"
      isFooterHidden
      isHeaderHidden
    >
      <Formik<AddressValidationFormValues>
        initialValues={initialValues}
        initialTouched={initialTouched}
        validationSchema={createAddressValidationModalSchema(t, result.fields, lastCheckedInput)}
        validateOnMount
        onSubmit={(values) =>
          onSubmit({
            address1: values.validationLine1,
            address2: values.validationLine2,
            city: values.validationCity,
            state: values.validationState,
            postalCode: values.validationPostalCode,
            country: values.validationCountry,
            formattedAddress: '',
          })
        }
      >
        <AddressValidationFields result={result} onSuggestionSelected={onSuggestionSelected} />
      </Formik>
    </Modal>
  );
};
